/// <amd-module name="Feed/Core/Redux/FeedHub/FeedHubSagas" />
import { SagaIterator } from "redux-saga";
import { effects } from "redux/redux-saga.compat";
import { Action, ActionWithPayload } from "redux/Utils/ActionHelper";

import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { getServiceLocation, routeUrl } from "VSS/Platform/Location";
import { IPageData } from "VSS/Platform/Page";
import { UserClaims, userHasClaim } from "VSS/Platform/UserClaims";
import { combineUrlPaths, Uri } from "VSS/Platform/Util/Url";
import { getVssPageData } from "VSS/Redux/Modules/VSSPageData";

import { DataProviderConstants, FeedServiceInstanceId, PackagingServiceInstanceId } from "Feed/Common/Constants/Constants";
import { Feed } from "Feed/Common/Contracts/Feed";
import {
    FeedMessage,
    FeedMessageData,
    UpstreamHealthStatus,
    UpstreamStatusCategory,
    WebPageData
} from "Feed/Common/Contracts/WebPage";
import * as Resources from "Feed/Common/Resources";
import { IHubState, NavigationActions } from "Feed/Core/Redux/Navigation";
import { IUxFeedView } from "Feed/Core/UxFeedView";
import { IProjectData } from "Tfs/Platform/Page";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";

import { FeedHubActions, FeedHubActionTypes, FeedHubMetaTypes, IFeedHubActionOptions } from "./FeedHubActions";
import { FeedHubData } from "./FeedHubContracts";
import {
    getSelectedFeedMessages,
    getSelectedUxFeedView,
    getSelectedUxParentFeed,
    getUpstreamHealthStatuses,
    isSelectedFeedDeleted
} from "./FeedHubSelectors";

export default function* rootSaga(): SagaIterator {
    yield effects.takeEvery(FeedHubActionTypes.LoadFeedMessageStarted, loadFeedMessage);
    yield effects.takeEvery(
        (action: Action<any>) => action.meta && action.meta[FeedHubMetaTypes.FEED_HUBACTION],
        feedPageChange
    );

    yield effects.call(loadInitialDataFromDataProvider);
}

function* getPackagingUrl(pageContext: IVssPageContext, publicAccessMapping: string): SagaIterator {
    // service location for onprem is /tfs/DefaultCollection/ and hosted is https://dev.azure.com/<orgname>/
    const url: string = getServiceLocation(pageContext, PackagingServiceInstanceId);

    const vssPageData: IPageData = (yield effects.select(getVssPageData)) as any;
    if (vssPageData.isHosted) {
        return new Uri(url).absoluteUri;
    } else {
        // in onprem, host is not returned so use the public access mapping in dataprovider to construct url
        const publicAccessMappingUrl = new Uri(publicAccessMapping);
        // todo: seems hacky!
        publicAccessMappingUrl.path = "";
        publicAccessMappingUrl.queryString = "";
        publicAccessMappingUrl.hashString = "";

        return combineUrlPaths(publicAccessMappingUrl.absoluteUri, url);
    }
}

function* getSessionTokenUrl(pageContext: IVssPageContext): SagaIterator {
    const rawUrl = (yield effects.call(
        routeUrl,
        pageContext,
        "ms.vss-tfs-web.user-settings-tokens-default-route"
    )) as any;
    return new Uri(rawUrl);
}

export function* loadInitialDataFromDataProvider(): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;

    try {
        const project: IProjectData = (yield effects.select(currentProject)) as any;
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");

        const webPageData: WebPageData = (yield effects.call(
            [contributionService, contributionService.getDataAsync],
            DataProviderConstants.PackageHubDataProvider,
            undefined,
            true,
            FeedServiceInstanceId
        )) as any;

        // only displaying feeds which are in current project or collection level
        const feedsToDisplay: Feed[] = webPageData.feedsResult.feeds.filter(
            feed => !feed.project || Utils_String.equals(feed.project.id, project.id, /*ignoreCase*/ true)
        );
        webPageData.feedsResult.feeds = feedsToDisplay;

        const isPublic = userHasClaim(pageContext, UserClaims.Public);
        const isAnonymous = userHasClaim(pageContext, UserClaims.Anonymous);
        const packagingUrl = yield* getPackagingUrl(pageContext, webPageData.publicAccessMapping);
        const sessionTokenUri: Uri = yield* getSessionTokenUrl(pageContext);

        const feedHubData: FeedHubData = { ...webPageData, isPublic, isAnonymous, packagingUrl, sessionTokenUri };

        yield effects.put(FeedHubActions.initialDataLoaded(feedHubData));
        yield effects.call(loadFeedMessage);
    } catch (error) {
        yield effects.put(FeedHubActions.initialDataLoadFailed(error));
    }
}

export function* loadFeedMessage(): SagaIterator {
    const feed: IUxFeedView = ((yield effects.select(getSelectedUxParentFeed)) as unknown) as IUxFeedView;
    const isFeedDeleted: IUxFeedView = ((yield effects.select(isSelectedFeedDeleted)) as unknown) as IUxFeedView;
    if (feed == undefined || isFeedDeleted) {
        return;
    }

    const currentFeedMessages: FeedMessage[] = ((yield effects.select(
        getSelectedFeedMessages
    )) as unknown) as FeedMessage[];
    if (currentFeedMessages != undefined &&
        ((currentFeedMessages.length > 0 && currentFeedMessages[0].id.includes(feed.id))
            || currentFeedMessages.length === 0)) {
        // If current feed already has a a feed message, the feed message data provider has already been called.
        return;
    }

    const upstreamStatuses = ((yield effects.select(getUpstreamHealthStatuses)) as unknown) as UpstreamHealthStatus[];
    if (!!upstreamStatuses && upstreamStatuses.length > 0) {
        // If upstream statuses have been set for the feed, the feed message data provider has already been called.
        return;
    }

    const properties = {
        feedId: feed.id,
        projectName: feed.project ? feed.project.name : null
    };

    try {
        // Call data provider for feed message
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const messageData: FeedMessageData = ((yield effects.call(
            [contributionService, contributionService.getDataAsync],
            DataProviderConstants.FeedMessagesDataProvider,
            properties,
            true /* ignoreCache */,
            FeedServiceInstanceId
        )) as unknown) as FeedMessageData;

        if (messageData == null) {
            // Set feed message to an empty array so that we know not to try to fetch it again for this feed
            yield effects.put(FeedHubActions.setFeedMessages(feed.id, []));
            return;
        }

        const messages = messageData.messages;
        if (messages && messages.length > 0) {
            const feedMessages: FeedMessage[] = [];
            messages.forEach(message => {
                const key = message.id;
                if (window.localStorage.getItem(key) !== key) {
                    // user hasn't dismissed the message, so update it:
                    feedMessages.push(message);
                }
            });

            if (feedMessages.length > 0) {
                yield effects.put(FeedHubActions.setFeedMessages(feed.id, feedMessages));
            }
        }

        if (messageData.additionalData && messageData.additionalData.upstreamStatuses.length > 0) {
            removeUpstreamBrowserLocalStorageIfNecessary(feed.id, messageData.additionalData.upstreamStatuses);

            // Update status in redux store
            yield effects.put(FeedHubActions.setUpstreamStatuses(feed.id, messageData.additionalData.upstreamStatuses));
        }
    } catch {
        // feed message isn't super important, so if things fail, keep going
        return;
    }
}

// Queue a navigation action to a hub for the selected feed
export function* feedPageChange(action: ActionWithPayload<any, any>): SagaIterator {
    const hubAction = action.meta![FeedHubMetaTypes.FEED_HUBACTION];
    const options = action.meta![FeedHubMetaTypes.FEED_HUBACTIONOPTIONS] as IFeedHubActionOptions;

    let feed: IUxFeedView | undefined;

    if (options != null && options.useParentFeedView === true) {
        feed = (yield effects.select(getSelectedUxParentFeed)) as any;
    } else {
        feed = (yield effects.select(getSelectedUxFeedView)) as any;
    }

    let feedName: string = "";
    if (feed != undefined) {
        feedName = feed.name;
        document.title = feedName;
    } else {
        document.title = Resources.HubTitle;
    }

    const newState: IHubState = {
        feed: feedName,
        action: hubAction
    };

    yield effects.put(FeedHubActions.clearErrorMessage());
    yield effects.put(NavigationActions.navigateToPage(newState));
    yield effects.put(FeedHubActions.loadFeedMessageStarted());
}

function removeUpstreamBrowserLocalStorageIfNecessary(feedId: string, upstreamStatuses: UpstreamHealthStatus[]) {
    const hasFailingSources = upstreamStatuses.some(
        status =>
            status.confidentStatus &&
            status.confidentStatus.statusCategory !== UpstreamStatusCategory.FullRefreshSuccess
    );

    if (!hasFailingSources) {
        // If no failing sources, make sure to clear local storage
        // so that any new upstream source failure after this will be shown again.
        const key = Utils_String.format("{0}-{1}", "UpstreamStatus", feedId);
        window.localStorage.removeItem(key);
    }
}
